import React from 'react';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  greyColours,
  brandColours,
  siteThemeColours,
  fontSize,
  zIndexLayers,
  visuallyHidden,
} from '../styles';
import { Container, Link, Svg } from './ui';
import Logo from './Logo';
import Navigation from './Navigation';
import phoneIcon from '../images/phone-icon.svg';
import basketIcon from '../images/basket-icon.svg';

const StyledHeader = styled.header`
  ${maxBreakpointQuery.mlarge`
    background-color: ${standardColours.white};
  `}

  ${minBreakpointQuery.mlarge`
    position: relative;
    z-index: ${zIndexLayers.third};
  `}

  ${({ backgroundColour }) => {
    if (backgroundColour) {
      return css`
        ${minBreakpointQuery.mlarge`
          background-color: ${backgroundColour};
        `}
      `;
    }
  }}

  ${({ alt }) => {
    if (alt) {
      return css`
        ${minBreakpointQuery.mlarge`
          position: absolute;
          top: 0;
          right: 0;
          left: 0;
          color: ${standardColours.white};
        `}
      `;
    } else {
      return css`
        ${maxBreakpointQuery.mlarge`
          border-bottom: 1px solid ${greyColours.lightPurplishGrey};
        `}
      `;
    }
  }}
`;

const StyledContainer = styled(Container)`
  ${maxBreakpointQuery.mlarge`
    padding-right: 0;
  `}

  ${minBreakpointQuery.tsmall`
    display: flex;
    flex-direction: column;
  `}

  ${minBreakpointQuery.xxlarge`
    padding-right: 40px;
    padding-left: 40px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    padding-right: 60px;
    padding-left: 60px;
  `}
`;

const StyledInner = styled.div`
  display: flex;
  align-items: center;

  ${minBreakpointQuery.mlarge`
    position: relative;
    gap: 15px;
    padding-top: 30px;
    padding-bottom: 30px;
    border-bottom: 1px solid ${({ alt, altTheme }) =>
      alt
        ? standardColours.transparentWhite(0.4)
        : altTheme
          ? greyColours.transparentGrey(0.2)
          : greyColours.grey};
  `}

  ${minBreakpointQuery.large`
    gap: 20px;
  `}

  ${minBreakpointQuery.xlarge`
    gap: 30px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 40px;
  `}
`;

const StyledLogo = styled(Logo)`
  ${minBreakpointQuery.mlarge`
    order: 1;
  `}
`;

const StyledPhoneBasket = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;

  ${maxBreakpointQuery.mlarge`
    padding-right: 15px;
    padding-left: 15px;
  `}

  ${minBreakpointQuery.mlarge`
    order: 3;
  `}

  ${minBreakpointQuery.large`
    gap: 10px;
  `}
`;

const StyledPhone = styled.p`
  ${minBreakpointQuery.mlarge`
    text-align: right;
  `}
`;

const StyledPhoneText = styled.span`
  ${maxBreakpointQuery.mlarge`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.mlarge`
    display: block;
    margin-bottom: 2px;
    ${fontSize(11)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(12)};
  `}
`;

const StyledPhoneLink = styled.a`
  ${maxBreakpointQuery.mlarge`
    display: block;
    padding: 5px;
  `}
`;

const StyledPhoneIcon = styled(Svg)`
  ${maxBreakpointQuery.mlarge`
    height: 25px;
    width: 25px;
    fill: ${brandColours.primary};
  `}

  ${maxBreakpointQuery.tsmall`
    height: 20px;
    width: 20px;
  `}

  ${minBreakpointQuery.mlarge`
    ${visuallyHidden()};
  `}
`;

const StyledPhoneNumber = styled.span`
  ${maxBreakpointQuery.mlarge`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.mlarge`
    ${fontSize(17)};
    white-space: nowrap;
  `}

  ${minBreakpointQuery.large`
    ${fontSize(18)};
  `}
`;

const StyledBasket = styled.div`
  position: relative;
`;

const StyledBasketLink = styled(Link)`
  padding: 5px;
`;

const StyledBasketIcon = styled(Svg)`
  height: 20px;
  width: 20px;
  fill: ${brandColours.primary};

  ${minBreakpointQuery.tsmall`
    height: 25px;
    width: 25px;
  `}

  ${({ alt }) => {
    if (alt) {
      return css`
        ${minBreakpointQuery.mlarge`
          fill: ${standardColours.white};
        `}
      `;
    }
  }}
`;

const StyledBasketNumber = styled.span`
  position: absolute;
  bottom: 5px;
  left: 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 12px;
  width: 12px;
  color: ${brandColours.primary};
  ${fontSize(8)};
  line-height: 1;
  background-color: ${brandColours.quaternary};
  border-radius: 50%;

  ${minBreakpointQuery.tsmall`
    height: 15px;
    width: 15px;
    ${fontSize(10)};
  `}
`;

const StyledNavigation = styled(Navigation)`
  ${minBreakpointQuery.mlarge`
    order: 2;
  `}
`;

const StyledCta = styled.p`
  padding: 6px 18px;
  color: ${standardColours.white};
  ${fontSize(12)};
  letter-spacing: 0.75px;
  text-transform: uppercase;
  text-align: center;
  background-color: ${({ siteTheme }) =>
    siteTheme === 'valentines'
      ? siteThemeColours.valentines.primary
      : siteTheme === 'mothersday'
        ? siteThemeColours.mothersDay.primary
        : siteTheme === 'summer'
          ? siteThemeColours.summer.primary
          : siteTheme === 'christmas'
            ? siteThemeColours.christmas.primary
            : siteTheme === 'blackfriday'
              ? brandColours.tertiary
              : brandColours.primary};

  a {
    text-decoration: underline;
  }

  ${maxBreakpointQuery.mlarge`
    margin-left: -20px;
  `}

  ${minBreakpointQuery.mlarge`
    margin-left: auto;
  `}
`;

const Header = ({
  telephone,
  cartItemCount,
  hasHeaderLabel,
  headerLabel,
  siteTheme,
  daysUntilEvent,
  backgroundColour,
  alt,
  altTheme,
  isNavActive,
  setNavActive,
}) => (
  <StyledHeader
    id="header"
    backgroundColour={backgroundColour}
    alt={alt}
    isNavActive={isNavActive}
  >
    <StyledContainer wide={true}>
      <StyledInner alt={alt} altTheme={altTheme}>
        <StyledLogo alt={alt || altTheme} />
        <StyledPhoneBasket>
          {telephone && (
            <StyledPhone>
              <StyledPhoneText>Bookings &amp; Advice</StyledPhoneText>
              <StyledPhoneLink href={`tel:${telephone}`}>
                <StyledPhoneIcon image={phoneIcon} />
                <StyledPhoneNumber>{telephone}</StyledPhoneNumber>
              </StyledPhoneLink>
            </StyledPhone>
          )}
          <StyledBasket>
            <StyledBasketLink to="/checkout/basket">
              <StyledBasketIcon image={basketIcon} alt={alt || altTheme} />
              <StyledBasketNumber>{cartItemCount}</StyledBasketNumber>
            </StyledBasketLink>
          </StyledBasket>
        </StyledPhoneBasket>
        <StyledNavigation
          alt={alt}
          isNavActive={isNavActive}
          setNavActive={setNavActive}
        />
      </StyledInner>
      {hasHeaderLabel && (
        <StyledCta siteTheme={siteTheme}>
          {siteTheme === 'valentines' ? (
            <>
              {headerLabel.countdown && daysUntilEvent ? (
                <>{daysUntilEvent} until Valentines! </>
              ) : (
                <>{headerLabel.message} </>
              )}
            </>
          ) : siteTheme === 'mothersday' ? (
            <>
              {headerLabel.countdown && daysUntilEvent ? (
                <>{daysUntilEvent} until Mother's Day </>
              ) : (
                <>{headerLabel.message} </>
              )}
            </>
          ) : siteTheme === 'christmas' ? (
            <>
              {headerLabel.countdown && daysUntilEvent ? (
                <>{daysUntilEvent} until Christmas </>
              ) : (
                <>{headerLabel.message} </>
              )}
            </>
          ) : siteTheme === 'blackfriday' ? (
            <>
              {headerLabel.countdown && daysUntilEvent ? (
                <>{daysUntilEvent} until Black Friday </>
              ) : (
                <>{headerLabel.message} </>
              )}
            </>
          ) : daysUntilEvent ? (
            <>{daysUntilEvent} until the Event </>
          ) : (
            <>{headerLabel.message} </>
          )}
          {headerLabel.linkText && (
            <Link
              to={
                siteTheme === 'valentines'
                  ? headerLabel.linkUrl || '/packages/valentines'
                  : siteTheme === 'summer'
                    ? headerLabel.linkUrl || '/packages/summer'
                    : headerLabel.linkUrl || '/spa-deals'
              }
            >
              {siteTheme === 'summer'
                ? headerLabel.linkText || 'View Summer of Spa offers'
                : siteTheme === 'christmas' && headerLabel.countdown
                  ? headerLabel.linkText || 'View gift ideas'
                  : headerLabel.linkText || 'View offers'}
            </Link>
          )}
        </StyledCta>
      )}
    </StyledContainer>
  </StyledHeader>
);

export default Header;
